// src/app/public-home/AccessRequestForm.tsx

import * as React from "react";

import {
  ACCESS_ROLE_OPTIONS,
  INITIAL_ACCESS_REQUEST_VALUES,
} from "./publicHome.constants";

import {
  submitAccessRequest,
  validateAccessRequest,
} from "./publicHome.helpers";

import type {
  AccessRequestFormErrors,
  AccessRequestFormValues,
} from "./publicHome.types";

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export function AccessRequestForm():
React.JSX.Element {
  const [
    values,
    setValues,
  ] =
    React.useState<AccessRequestFormValues>(
      INITIAL_ACCESS_REQUEST_VALUES,
    );

  const [
    errors,
    setErrors,
  ] =
    React.useState<AccessRequestFormErrors>(
      {},
    );

  const [
    submitError,
    setSubmitError,
  ] =
    React.useState<
      string | null
    >(
      null,
    );

  const [
    submitting,
    setSubmitting,
  ] =
    React.useState(
      false,
    );

  const [
    submitted,
    setSubmitted,
  ] =
    React.useState(
      false,
    );

  const updateField =
    <K extends keyof AccessRequestFormValues>(
      field:
        K,

      value:
        AccessRequestFormValues[K],
    ) => {
      setValues(
        (
          current,
        ) => ({
          ...current,

          [field]:
            value,
        }),
      );

      setErrors(
        (
          current,
        ) => ({
          ...current,

          [field]:
            undefined,
        }),
      );

      setSubmitError(
        null,
      );
    };

  const handleSubmit =
    async (
      event:
        React.FormEvent<HTMLFormElement>,
    ) => {
      event.preventDefault();

      const nextErrors =
        validateAccessRequest(
          values,
        );

      setErrors(
        nextErrors,
      );

      setSubmitError(
        null,
      );

      if (
        Object.keys(
          nextErrors,
        ).length > 0
      ) {
        return;
      }

      setSubmitting(
        true,
      );

      try {
        await submitAccessRequest(
          values,
        );

        setSubmitted(
          true,
        );
      } catch (
        error
      ) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : "We couldn’t send your request. Try again.",
        );
      } finally {
        setSubmitting(
          false,
        );
      }
    };

  if (
    submitted
  ) {
    return (
      <p
        className="access-success"
        role="status"
        aria-live="polite"
      >
        Thanks. We’ve received your request and will be in touch.
      </p>
    );
  }

  return (
    <form
      className="access-form"
      noValidate
      onSubmit={
        (
          event,
        ) =>
          void handleSubmit(
            event,
          )
      }
    >
      <div className="public-field">
        <label
          htmlFor="access-name"
          className="public-label"
        >
          Name
        </label>

        <input
          id="access-name"
          name="name"
          type="text"
          autoComplete="name"
          className="public-input"
          value={
            values.name
          }
          disabled={
            submitting
          }
          aria-invalid={
            Boolean(
              errors.name,
            )
          }
          aria-describedby={
            errors.name
              ? "access-name-error"
              : undefined
          }
          onChange={
            (
              event,
            ) =>
              updateField(
                "name",
                event.target.value,
              )
          }
        />

        {errors.name ? (
          <p
            id="access-name-error"
            className="public-field-error"
          >
            {errors.name}
          </p>
        ) : null}
      </div>

      <div className="public-field">
        <label
          htmlFor="access-email"
          className="public-label"
        >
          Email
        </label>

        <input
          id="access-email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          className="public-input"
          value={
            values.email
          }
          disabled={
            submitting
          }
          aria-invalid={
            Boolean(
              errors.email,
            )
          }
          aria-describedby={
            errors.email
              ? "access-email-error"
              : undefined
          }
          onChange={
            (
              event,
            ) =>
              updateField(
                "email",
                event.target.value,
              )
          }
        />

        {errors.email ? (
          <p
            id="access-email-error"
            className="public-field-error"
          >
            {errors.email}
          </p>
        ) : null}
      </div>

      <fieldset
        className="public-field public-role-field"
        disabled={
          submitting
        }
        aria-describedby={
          errors.role
            ? "access-role-error"
            : undefined
        }
      >
        <legend className="public-label">
          Your role
        </legend>

        <div className="public-radio-group">
          {ACCESS_ROLE_OPTIONS.map(
            (
              option,
            ) => (
              <label
                key={
                  option.value
                }
                className="public-radio-option"
              >
                <input
                  type="radio"
                  name="role"
                  value={
                    option.value
                  }
                  checked={
                    values.role ===
                    option.value
                  }
                  onChange={
                    () =>
                      updateField(
                        "role",
                        option.value,
                      )
                  }
                />

                <span>
                  {option.label}
                </span>
              </label>
            ),
          )}
        </div>

        {errors.role ? (
          <p
            id="access-role-error"
            className="public-field-error"
          >
            {errors.role}
          </p>
        ) : null}
      </fieldset>

      <div className="public-field">
        <label
          htmlFor="access-looks-after"
          className="public-label"
        >
          What do you look after?

          <span className="public-optional">
            Optional
          </span>
        </label>

        <input
          id="access-looks-after"
          name="looksAfter"
          type="text"
          className="public-input"
          value={
            values.looksAfter
          }
          disabled={
            submitting
          }
          placeholder="e.g. 120 residential properties"
          onChange={
            (
              event,
            ) =>
              updateField(
                "looksAfter",
                event.target.value,
              )
          }
        />
      </div>

      <button
        type="submit"
        className="public-button public-button-primary access-submit"
        disabled={
          submitting
        }
      >
        {submitting
          ? "Sending"
          : "Request access"}
      </button>

      {submitError ? (
        <p
          className="public-submit-error"
          role="alert"
        >
          {submitError}
        </p>
      ) : null}
    </form>
  );
}